import React from 'react'
import Header from './Header'
import Footer from './Footer'

function Terms() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-white flex flex-col">
      <Header />
      
      <div className="flex-1 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Use</h1>
            
            <div className="space-y-6 text-gray-700">
              <div className="mb-6">
                <p className="text-sm text-gray-500 mb-4">
                  <strong>Effective Date:</strong> 30 June 2025
                </p>
                <p className="mb-4">
                  Operated by Code ESX IT LLC, trading as Coded Estate, a Dubai mainland company, United Arab Emirates.
                </p>
              </div>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Agreement & Scope</h2>
                <p className="mb-4">
                  These Terms of Use ("Terms") constitute a legally binding agreement between you ("you," "User") and Code ESX IT LLC ("we," "us," "our"), governing your access to and use of the Coded Estate platform—including our websites, mobile apps, APIs, smart contracts, decentralized features, and any third-party integrations (collectively, the "Platform"). By accessing or using the Platform in any way (browsing, interacting, booking, listing, trading, etc.), you affirm that you have read, understood, and agree to be bound by these Terms, as well as our Privacy Policy and any supplemental policies. If you do not agree, you must immediately cease using the Platform.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Eligibility & Restricted Territories</h2>
                <p className="mb-4"><strong>2.1 You warrant that:</strong></p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>You are at least 18 years old and legally capable of forming binding contracts under the laws of your jurisdiction;</li>
                  <li>You do not reside in, are not a citizen of, are not located in, are not incorporated in, and do not access the Platform from any Restricted Territory (including: United States, North Korea, Syria, Iran, Iraq, Libya, Sudan, Somalia, Yemen, Russia, Venezuela, Cuba, Myanmar, Crimea, Donetsk, Luhansk, and any country subject to comprehensive US, UAE, UN, or EU sanctions), nor are you a Sanctioned Person under any applicable sanctions regime;</li>
                  <li>You will not use VPNs, proxies, or any network obfuscation tools to circumvent territorial restrictions;</li>
                </ul>
                <p className="mb-4">
                  Failure to comply will result in immediate termination of your access and rights, at our sole discretion.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Updates to Terms</h2>
                <p className="mb-4">
                  We reserve the right, at our sole discretion, to change, add to, or remove parts of these Terms at any time. Changes will take effect upon posting the updated version to the Platform with an updated "Effective Date." Continuing to use the Platform after updates indicates your acceptance.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Overview of Services</h2>
                <p className="mb-4"><strong>4.1 The Platform enables:</strong></p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>Property listings, searches, bookings, and related real-estate facilitation services;</li>
                  <li>Access to DeFi tools (synthetic property indexes, DLT-based perpetual contracts, non-custodial data feeds);</li>
                  <li>Access to analytics, transaction history, wallet integrations, market-level features.</li>
                </ul>
                <p className="mb-4">
                  <strong>4.2</strong> We act solely as a digital intermediary. We are not a party to underlying contracts, custodian of funds, escrow agent, financial advisor, broker, or licensed real estate agent. All buying, selling, trading, or booking transactions occur between users and third-party providers.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Account, KYC & Security</h2>
                <p className="mb-4">
                  <strong>5.1</strong> To use non-public features (booking, verifying, trading), you may be required to register for an account and provide personally identifiable information ("PII"). You are fully responsible for maintaining confidentiality of login credentials, wallets, keys, or authentication methods.
                </p>
                <p className="mb-4">
                  <strong>5.2</strong> We may require you to complete KYC (Know Your Customer) and AML (Anti-Money Laundering) procedures—collecting documents and data as legally required. Failure to comply may result in account suspension, transaction cancellation, or reporting to authorities.
                </p>
                <p className="mb-4">
                  <strong>5.3</strong> You are responsible for all activities under your account or wallet. Promptly notify us if you suspect unauthorized access. We are not liable for losses due to compromised credentials.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Listings & Bookings</h2>
                <p className="mb-4">
                  <strong>6.1</strong> Hosts represent and warrant full legal authority to list and transact property on the Platform.
                </p>
                <p className="mb-4">
                  <strong>6.2</strong> We do not vet or verify listings, hosts, or their claims, nor endorse, guarantee, or inspect any properties.
                </p>
                <p className="mb-4">
                  <strong>6.3</strong> All bookings are strictly interactions between guests and hosts; we do not mediate contracts or impose guarantees unless explicitly stated otherwise.
                </p>
                <p className="mb-4">
                  <strong>6.4</strong> You must resolve disputes with hosts or guests independently—except where supplementary mediation services are offered under separate agreement. We bear no liability for disputes, injuries, damages, or unsatisfactory experiences.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Payments & Refunds</h2>
                <p className="mb-4">
                  <strong>7.1</strong> All payments are processed by third-party providers. We do not hold or manage funds; we act only as a facilitator.
                </p>
                <p className="mb-4">
                  <strong>7.2</strong> Use of digital assets, stablecoins, or fiat currencies is permitted within our allowed payment methods—subject to wallet custodians like BitGo, credit card networks, or gateway services.
                </p>
                <p className="mb-4">
                  <strong>7.3</strong> Cryptocurrency payments are irreversible and non-custodial. We do not provide exchange rates, price guarantees, or volatility hedges.
                </p>
                <p className="mb-4">
                  <strong>7.4</strong> Refunds, cancellations, or transaction reversals are subject solely to the policies of the relevant third-party payment or service provider. We expressly disclaim all responsibility for payment failures, delays, or unfulfilled refunds.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Decentralized Asset Features</h2>
                <p className="mb-4">
                  <strong>8.1</strong> Optional DLT tools (tokenized indexes, perpetual contracts, data feeds) do not constitute advisory services, custody, custodial wallet provision, or trading services.
                </p>
                <p className="mb-4">
                  <strong>8.2</strong> You acknowledge and assume full risk—including financial loss, technical malfunction, counterparty failure, smart contract vulnerabilities, regulatory change.
                </p>
                <p className="mb-4">
                  <strong>8.3</strong> We hold no financial services license (e.g., CASP, MSB, broker-dealer) under UAE or global jurisdictions. We disclaim any fiduciary duty or advisory liability.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Use & Prohibited Conduct</h2>
                <p className="mb-4">You may not:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Use the Platform unlawfully, in violation of rights or regulations;</li>
                  <li>Scrape, extract, reproduce, or broadcast Platform data;</li>
                  <li>Interfere with, reverse engineer, or hack Platform systems, or introduce malware;</li>
                  <li>Use anonymization tools to evade restrictions;</li>
                  <li>Engage in prohibited economic or financial activity (e.g., money laundering, terrorist financing, illicit goods, gambling);</li>
                  <li>Distribute infringing or unauthorized intellectual property;</li>
                  <li>Transmit malware, viruses, or harm network integrity;</li>
                  <li>Create multiple Accounts to evade restrictions or misconduct rules.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Intellectual Property</h2>
                <p className="mb-4">
                  We and our licensors own all rights to Platform content—logos, trademarks, software, design, data—protected by local and international IP law. You are granted a limited, revocable license to view and use the Platform in compliance with these Terms only. All rights not expressly granted are reserved.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Representations & Warranties</h2>
                <p className="mb-4">You represent that:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You provide accurate information, maintain its current status;</li>
                  <li>You will lawfully use the Platform;</li>
                  <li>Your use adheres to all applicable laws, and no third-party rights are violated;</li>
                  <li>Transaction or listing indicates full legal authority and compliance.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Disclaimer of Warranties & Limitation of Liability</h2>
                <p className="mb-4">
                  <strong>12.1</strong> Platform is provided "as‑is, as‑available," with no warranties of merchantability, fitness, accuracy, uptime, security, completeness, or non-infringement.
                </p>
                <p className="mb-4">
                  <strong>12.2</strong> We expressly disclaim liability for any loss—direct, indirect, consequential, incidental, punitive, or exemplary—arising from Platform use or inability to transact.
                </p>
                <p className="mb-4">
                  <strong>12.3</strong> To the extent permitted by UAE law, our maximum liability is capped at the total fees paid by you in the preceding 12 months. Under no circumstances will we be liable for lost profits, data loss, or business interruption.
                </p>
                <p className="mb-4">
                  <strong>12.4</strong> This limitation survives termination of these Terms.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">13. Indemnification</h2>
                <p className="mb-4">
                  You agree to indemnify, defend, and hold harmless Code ESX IT LLC, its affiliates, officers, directors, agents, employees, and partners from any claims, losses, damages, liabilities, or expenses (including legal fees) resulting from your violation of these Terms or misuse of the Platform.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">14. Governing Law & Dispute Resolution</h2>
                <p className="mb-4">
                  <strong>14.1</strong> These Terms are governed by UAE federal law, as applied in Dubai mainland courts.
                </p>
                <p className="mb-4">
                  <strong>14.2</strong> You agree to submit to the exclusive jurisdiction of the Dubai Courts, to resolve disputes via litigation under UAE procedural rules.
                </p>
                <p className="mb-4">
                  <strong>14.3</strong> Alternatively, we may require arbitration by the Dubai International Arbitration Centre (DIAC) under UAE arbitration law, binding and individual, without class actions.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">15. Force Majeure</h2>
                <p className="mb-4">
                  We are not liable for delays or failures arising from events beyond reasonable control—natural disasters, acts of war, governmental actions, system outages, pandemics.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">16. Severability, Waiver & Assignment</h2>
                <p className="mb-4">
                  <strong>16.1</strong> If any provision is invalid under UAE law, the remaining provisions survive.
                </p>
                <p className="mb-4">
                  <strong>16.2</strong> Failure to enforce a right does not waive it.
                </p>
                <p className="mb-4">
                  <strong>16.3</strong> You may not assign your rights without our express consent. We may assign these Terms at our discretion.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">17. Entire Agreement</h2>
                <p className="mb-4">
                  These Terms, together with our Privacy Policy and any referenced supplements, constitute the entire agreement and supersede prior understandings related to Platform access and use.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Contact Us</h2>
                <p className="mb-4">
                  For legal, compliance, or support inquiries:
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-medium">Email: legal@codedestate.com</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default Terms